import React, { useState, KeyboardEvent, SyntheticEvent, useRef } from 'react';
import { IMaskInput } from 'react-imask';
import cx from 'classnames';

import './input.css';
import { getInputBorderAndPadding } from './helpers';

interface NumberInputProps {
    /**
     * Optional classes to add to the input
     */
    addClasses?: string;
    /**
     * Optional config which will override the default classes on the input
     */
    classConfig?: InputClassConfig;
    /**
     * Optional decimal places to allow in input (defaults to 2)
     */
    decimalPlaces?: number;
    /**
     * ID for the input
     */
    inputId?: string;
    /**
     * Current state of the input. Although this can be undefined, you should always manage it with
     * useState in the parent and ensure the type signature of the state variable is
     * `number | undefined`
     */
    value?: number;
    /**
     * Optional onBlur handler, passed directly to <input>
     */
    onBlur?(e: SyntheticEvent): void;
    /**
     * A method to call when the input changes. It will be given the numeric value (not the
     * formatted string). This should always call the setState function provided by useState in the
     * parent.
     * @param value - next value of the input
     */
    onChange(value: number): void;
    /**
     * Optional onFocus handler, passed directly to <input>
     */
    onFocus?(e: SyntheticEvent): void;
    /**
     * Optional max value
     * @default Number.MAX_SAFE_INTEGER
     */
    max?: number;
    /**
     * Optional min value
     * @default -Number.MAX_SAFE_INTEGER
     */
    min?: number;
    /**
     * Padding to apply to the input
     * @default "small"
     */
    padding?: 'small' | 'large';

    /**
     * Optional width
     */
    width?: number;
}

/**
 * An input that is set up to work with numeric amounts.
 * @param props - see {@link NumberInputProps}
 */
export const NumberInput = (props: NumberInputProps): JSX.Element => {
    const {
        addClasses,
        classConfig,
        decimalPlaces = 2,
        inputId,
        value,
        max = Number.MAX_SAFE_INTEGER,
        min = -Number.MAX_SAFE_INTEGER,
        onBlur,
        onChange,
        onFocus,
        padding = 'small',
        width,
    } = props;
    const [hasFocus, setHasFocus] = useState(false);
    const borderAndPadding = getInputBorderAndPadding({
        classConfig,
        hasFocus,
        padding,
    });
    const classNames = cx(borderAndPadding, addClasses);
    const inputRef = useRef<{ element: HTMLInputElement }>(null);

    return (
        <IMaskInput
            className={classNames}
            ref={inputRef}
            id={inputId}
            mask={Number}
            max={max}
            min={min}
            // Because `unmask` is set to "typed" and `mask` is set to `Number`, the value here
            // is a number
            onAccept={(nextValue: number): void => {
                onChange(nextValue);
            }}
            onBlur={(e: SyntheticEvent): void => {
                if (onBlur) {
                    onBlur(e);
                }

                setHasFocus(false);
            }}
            onFocus={(e: SyntheticEvent): void => {
                if (onFocus) {
                    onFocus(e);
                }

                setHasFocus(true);
            }}
            onKeyPress={(e: KeyboardEvent<HTMLElement>): void => {
                if (e.key === 'Enter') {
                    if (inputRef.current) {
                        inputRef.current.element.blur();
                    }
                }
            }}
            // It would be nice to add `padFractionalZeroes` here, but React throws an error
            // if we use it. See docs: https://imask.js.org/guide.html#masked-number
            radix="."
            scale={decimalPlaces}
            style={{
                textAlign: 'end',
                width,
            }}
            thousandsSeparator=","
            unmask="typed"
            value={value}
        />
    );
};
